import { BeekSessionStatus, getSessionStatus } from 'src/utils/user';
import { ENV } from 'src/utils/env';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { getChallengeIdSS } from 'src/app/auth/cookiesServerSide';
import { hasSeenOnboarding } from 'src/api/user/dataUser';
import { useEffect } from 'react';
import Avo from 'src/tracking/Avo';
import Layout from 'src/components/Welcome/Layout';
import SignupForm from 'src/components/Authentication/Signup';
import UnbounceScript from 'src/components/UnbounceScript/UnbounceScript';
import pathWithQueryString from 'src/common/utils/pathWithQueryString';

const IndexPage = (): JSX.Element => {
  useEffect(() => {
    // Attempting to avert a NextJS race condition
    setTimeout(() => {
      try {
        Avo.signupFlowStarted({
          origin: 'Signup Screen',
        });
      } catch (error) {
        if (ENV !== 'production') {
          console.error('Avo Error:', error);
        }
      }
    }, 3000);
  }, []);

  return (
    <Layout currentStep="Signup">
      <>
        <UnbounceScript />
        <SignupForm />
      </>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const session = await getSessionStatus(context);

  // If user is logged-in and already subscribed, redirect to home
  if (session === BeekSessionStatus.LoggedInWithSubscription) {
    return {
      redirect: {
        destination: '/home',
        permanent: false,
      },
    };
  }

  if (session === BeekSessionStatus.LoggedIn) {
    // If user is not completed onboarding,
    // redirect to the beginning of the onboarding flow.
    const userHasOnboarded = await hasSeenOnboarding(context);

    const { offer } = context.query;
    const challengeIdCookie = getChallengeIdSS(context);

    const onboarding_path = pathWithQueryString('/welcome/choose-your-path', {
      offer,
    });
    const challenge_onboarding_path = pathWithQueryString(
      '/welcome/challenge-onboarding',
      {
        offer,
      }
    );

    if (!userHasOnboarded && challengeIdCookie) {
      return {
        redirect: {
          destination: challenge_onboarding_path,
          permanent: false,
        },
      };
    }

    if (!userHasOnboarded) {
      return {
        redirect: {
          destination: onboarding_path,
          permanent: false,
        },
      };
    }

    // If the user is logged in, has completed onboarding, but hasn't subscribed yet,
    // redirect to the subscription flow.
    const subscription_path = pathWithQueryString('/subscription/start', {
      offer,
    });
    return {
      redirect: {
        destination: subscription_path,
        permanent: false,
      },
    };
  }

  // If the user is logged-out, proceed to Signup page as normal.
  return {
    props: {
      messages: {
        ...require(`src/messages/article/${context.locale}.json`),
        ...require(`src/messages/auth/${context.locale}.json`),
      },
    },
  };
};

export default IndexPage;
